import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { saveNormalizedGroup, deleteNormalizedGroup, type NormalizedGroup } from '@/services/data-integration-api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface NormalizedGroupPanelProps {
  groups: NormalizedGroup[];
  unclassifiedCount: number;
  selectedGroupId: string | null | undefined; // undefined = 全部, null = 未分类
  onSelectGroup: (groupId: string | null | undefined) => void;
  onGroupsChanged: () => void;
}

export default function NormalizedGroupPanel({
  groups,
  unclassifiedCount,
  selectedGroupId,
  onSelectGroup,
  onGroupsChanged,
}: NormalizedGroupPanelProps) {
  const [newGroupName, setNewGroupName] = useState('');
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<NormalizedGroup | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleCreate = async () => {
    if (!newGroupName.trim()) return;
    setCreating(true);
    try {
      await saveNormalizedGroup({ groupName: newGroupName.trim() });
      toast.success(`分组"${newGroupName}"已创建`);
      setNewGroupName('');
      setShowCreate(false);
      onGroupsChanged();
    } catch (e: any) {
      toast.error('创建失败: ' + e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (groupId: string) => {
    if (!editingName.trim()) return;
    try {
      await saveNormalizedGroup({ groupId, groupName: editingName.trim() });
      toast.success('重命名成功');
      setEditingId(null);
      onGroupsChanged();
    } catch (e: any) {
      toast.error('重命名失败: ' + e.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteNormalizedGroup({ groupId: deleteTarget._id });
      toast.success(`分组已删除，${result.tables_ungrouped} 张表已回归未分类`);
      if (selectedGroupId === deleteTarget._id) onSelectGroup(null);
      setDeleteTarget(null);
      onGroupsChanged();
    } catch (e: any) {
      toast.error('删除失败: ' + e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="w-52 shrink-0 border-r border-border flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">业务领域分组</span>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowCreate(v => !v)} title="新建分组">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      {showCreate && (
        <div className="px-3 py-2 border-b border-border flex gap-1">
          <Input
            className="h-7 text-xs flex-1"
            placeholder="分组名称"
            value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') setShowCreate(false); }}
            autoFocus
          />
          <Button size="sm" className="h-7 px-2" onClick={handleCreate} disabled={creating}>
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setShowCreate(false)}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      <nav className="flex-1 overflow-y-auto py-1">
        {/* 全部 */}
        <button
          onClick={() => onSelectGroup(undefined as any)}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-1.5 text-sm transition-colors',
            selectedGroupId === undefined
              ? 'border-l-2 border-l-primary font-medium text-foreground bg-accent/50'
              : 'border-l-2 border-l-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
          )}
        >
          <Layers className="h-3.5 w-3.5 shrink-0" />
          <span className="flex-1 text-left truncate">全部</span>
          <Badge variant="secondary" className="text-[10px] h-4 px-1">{groups.reduce((s, g) => s + g.table_count, 0) + unclassifiedCount}</Badge>
        </button>

        {/* 未分类 */}
        <button
          onClick={() => onSelectGroup(null)}
          className={cn(
            'flex items-center gap-2 w-full px-3 py-1.5 text-sm transition-colors',
            selectedGroupId === null
              ? 'border-l-2 border-l-primary font-medium text-foreground bg-accent/50'
              : 'border-l-2 border-l-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
          )}
        >
          <span className="w-3.5 h-3.5 shrink-0 text-center text-xs leading-none">—</span>
          <span className="flex-1 text-left truncate">未分类</span>
          <Badge variant="outline" className="text-[10px] h-4 px-1">{unclassifiedCount}</Badge>
        </button>

        {/* 各分组 */}
        {groups.map(group => (
          <div key={group._id} className="group/item">
            {editingId === group._id ? (
              <div className="flex items-center gap-1 px-2 py-1">
                <Input
                  className="h-6 text-xs flex-1"
                  value={editingName}
                  onChange={e => setEditingName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleRename(group._id); if (e.key === 'Escape') setEditingId(null); }}
                  autoFocus
                />
                <button onClick={() => handleRename(group._id)} className="text-primary hover:text-primary/80">
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setEditingId(null)} className="text-muted-foreground hover:text-foreground">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => onSelectGroup(group._id)}
                className={cn(
                  'flex items-center gap-2 w-full px-3 py-1.5 text-sm transition-colors',
                  selectedGroupId === group._id
                    ? 'border-l-2 border-l-primary font-medium text-foreground bg-accent/50'
                    : 'border-l-2 border-l-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
                )}
              >
                <span className="flex-1 text-left truncate">{group.group_name}</span>
                <Badge variant="secondary" className="text-[10px] h-4 px-1 shrink-0">{group.table_count}</Badge>
                <span className="hidden group-hover/item:flex items-center gap-0.5 shrink-0">
                  <span
                    onClick={e => { e.stopPropagation(); setEditingId(group._id); setEditingName(group.group_name); }}
                    className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                  >
                    <Pencil className="h-3 w-3" />
                  </span>
                  <span
                    onClick={e => { e.stopPropagation(); setDeleteTarget(group); }}
                    className="p-0.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" />
                  </span>
                </span>
              </button>
            )}
          </div>
        ))}
      </nav>

      <Dialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>删除分组"{deleteTarget?.group_name}"？</DialogTitle>
            <DialogDescription>
              该分组下的 {deleteTarget?.table_count} 张规范存取表将自动回归"未分类"。此操作不可撤销。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>取消</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? '删除中...' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
